/** SPEC-OPP-020 — o board, do ponto de vista do cliente.
 *
 * Os tipos descrevem **o que a API devolve hoje**, e não o exemplo da
 * `SPEC.md § SPEC-OPP-020`. Os dois divergem: a spec ilustra `stage` e `owner`
 * como objetos aninhados; a implementação achata em `stage_id`/`stage_name` e
 * `owner_id`/`owner_name`, e não tem `avatar_url`. Construir contra o exemplo
 * daria uma tela que quebra na primeira chamada.
 */

import { http } from "@/lib/http";
import type { components } from "@/lib/api-types";

export type Totais = components["schemas"]["TotaisDaColunaResponse"];

export type StatusDaOportunidade = "open" | "won" | "lost";
export type Temperatura = "hot" | "warm" | "cold";

export type TipoDeEtapa = "open" | "won" | "lost";

/** `status`, `temperature` e (em `Coluna`) `stage_type` são `string` no
 * contrato — a saída não usa `Literal`. O front sempre assumiu as uniões
 * fechadas abaixo, que são os únicos valores que o domínio produz. */
export type Card = Omit<components["schemas"]["CardDoBoardResponse"], "status" | "temperature"> & {
  status: StatusDaOportunidade;
  temperature: Temperatura | null;
};

export type Coluna = Omit<
  components["schemas"]["ColunaDoBoardResponse"],
  "stage_type" | "cards"
> & {
  stage_type: TipoDeEtapa;
  cards: Card[];
};

export type Quadro = Omit<components["schemas"]["BoardResponse"], "columns"> & {
  columns: Coluna[];
};

export interface FiltrosDoQuadro {
  busca?: string;
  ownerId?: string;
  /** Nome da etiqueta, como a pessoa a vê (SPEC-OPP-050). */
  tag?: string;
}

export async function buscarQuadro(filtros: FiltrosDoQuadro = {}): Promise<Quadro> {
  const parametros = new URLSearchParams();
  if (filtros.busca !== undefined && filtros.busca.trim() !== "") {
    parametros.set("q", filtros.busca.trim());
  }
  if (filtros.ownerId !== undefined) parametros.set("owner_id", filtros.ownerId);
  // Repetido nas duas chamadas de propósito: sem ele, a página seguinte de
  // uma coluna traz cards de fora do filtro, e cada um isolado parece
  // legítimo (SPEC-OPP-020 § 3.2).
  if (filtros.tag !== undefined && filtros.tag !== "") parametros.set("tag", filtros.tag);

  const consulta = parametros.toString();
  return http.get<Quadro>(`/opportunities/board${consulta === "" ? "" : `?${consulta}`}`);
}

export type PaginaDaColuna = Omit<components["schemas"]["ColunaPaginadaResponse"], "cards"> & {
  cards: Card[];
};

/** Mais cards de uma coluna (SPEC-OPP-020 § 3).
 *
 * Os filtros vão junto e **não são opcionais na prática**: sem eles a segunda
 * página é de outra lista, e nada na tela denunciaria isso — cada card isolado
 * parece legítimo.
 */
export async function buscarMaisDaColuna(
  stageId: string,
  cursor: string,
  filtros: FiltrosDoQuadro & { pipelineId?: string } = {},
): Promise<PaginaDaColuna> {
  const parametros = new URLSearchParams({ cursor });
  if (filtros.busca !== undefined && filtros.busca.trim() !== "") {
    parametros.set("q", filtros.busca.trim());
  }
  if (filtros.ownerId !== undefined) parametros.set("owner_id", filtros.ownerId);
  // Repetido nas duas chamadas de propósito: sem ele, a página seguinte de
  // uma coluna traz cards de fora do filtro, e cada um isolado parece
  // legítimo (SPEC-OPP-020 § 3.2).
  if (filtros.tag !== undefined && filtros.tag !== "") parametros.set("tag", filtros.tag);
  if (filtros.pipelineId !== undefined) parametros.set("pipeline_id", filtros.pipelineId);

  return http.get<PaginaDaColuna>(
    `/opportunities/board/columns/${encodeURIComponent(stageId)}/cards?${parametros.toString()}`,
  );
}

/** SPEC-OPP-022. Centavos em `number`; a formatação é da tela.
 *
 * Os campos anuláveis são anuláveis de propósito: `0` se leria como "os
 * negócios valem nada" e "0% de conversão" — desempenho ruim que não
 * aconteceu. Ausência de negócio e desempenho ruim são coisas diferentes, e a
 * barra mostra cada uma do seu jeito.
 */
export type Indicadores = components["schemas"]["IndicadoresResponse"];

export async function buscarMetricas(): Promise<Indicadores> {
  const resposta = await http.get<{ metrics: Indicadores }>("/opportunities/metrics");
  return resposta.metrics;
}

/** O contrato (`CriarOportunidadeRequest`) também aceita `company_id`,
 * `currency`, `pipeline_id`, `primary_contact_id` e `probability` — nenhuma
 * tela usa esses campos hoje, e como são todos opcionais o tipo antigo
 * (subconjunto) continua um `CriarOportunidadeRequest` válido. */
export type NovaOportunidade = components["schemas"]["CriarOportunidadeRequest"];

export type Pessoa = components["schemas"]["PessoaResponse"];

/** SPEC-IDN-013. Apenas as pessoas ativas do tenant, com id e nome. */
export async function buscarPessoas(): Promise<Pessoa[]> {
  const resposta = await http.get<{ users: Pessoa[] }>("/users");
  return resposta.users;
}

/** O tipo antigo só declarava os cinco campos que os chamadores liam;
 * `OportunidadeResponse` traz bem mais (empresa, valores, datas, motivo de
 * perda). Superconjunto seguro — ninguém deixa de compilar por ganhar
 * campos. */
export type Oportunidade = components["schemas"]["OportunidadeResponse"];

/** SPEC-OPP-001. Só o título é obrigatório — o resto tem padrão do tenant. */
export async function criarOportunidade(dados: NovaOportunidade): Promise<Oportunidade> {
  return http.post<Oportunidade>("/opportunities", dados);
}

/** SPEC-OPP-014. `position` é o índice na coluna, contando do topo. */
export async function moverOportunidade(
  opportunityId: string,
  stageId: string,
  position?: number,
): Promise<Oportunidade> {
  return http.patch<Oportunidade>(`/opportunities/${opportunityId}/stage`, {
    stage_id: stageId,
    ...(position === undefined ? {} : { position }),
  });
}

/** SPEC-OPP-040 — perder exige motivo, e por isso tem rota própria.
 *
 * `PATCH /stage` para uma etapa de perda responde `422` sem `lost_reason_code`:
 * o domínio recusa fechar como perdida sem dizer por quê, porque é justamente o
 * motivo que permite descobrir depois por que se perde. O board mandava o
 * `PATCH` puro e a movimentação falhava — a rota certa sempre existiu e a tela
 * não a usava.
 */
export async function perderOportunidade(
  opportunityId: string,
  reasonCode: string,
  reasonNote?: string,
): Promise<Oportunidade> {
  return http.post<Oportunidade>(`/opportunities/${opportunityId}/lose`, {
    reason_code: reasonCode,
    ...(reasonNote === undefined || reasonNote.trim() === ""
      ? {}
      : { reason_note: reasonNote.trim() }),
  });
}
